import { entries, omit } from "lodash";
import { Reducer } from "redux";
import { Entities } from "~/normalizr/entities";
import { ObjectNotFoundAction } from "../../Actions/errorActions";
import { apiMiddlewareActionTypes } from "../../Middleware/apiMiddlewareActionTypes";
import { CreateEntitySuccess, DeleteEntities, EntitiesActions, EntitiesActionTypes } from "../actions";

export enum EntityNames {
    categories = "categories",
    calendars = "calendars",
    tasks = "tasks",
    projects = "projects",
    priorities = "priorities",
    vacationTypes = "vacationTypes",
    customers = "customers",
    roles = "roles",
    thresholds = "thresholds",
    presets = "presets",
    forms = "forms",
    formRecords = "formRecords",
    attachments = "attachments",
    overtimeThresholds = "overtimeThresholds",
    publicHolidays = "publicHolidays",
    users = "users",
    notifications = "notifications",
    teams = "teams",
    calendarTasks = "calendarTasks",
    vacations = "vacations",
    vacationTypesColors = "vacationTypesColors",
    customReports = "customReports",
    historyEntries = "historyEntries",
    rateCards = "rateCards",
}

export const mergeEntity = <T extends {}>(source: Entities<T>, data: Entities<T>): Entities<T> => {
    if (!data) {
        return source;
    }

    const result = { ...source };
    entries(data).forEach(([id, item]: [any, T]) => {
        const existingItem = result[id];
        result[id] = existingItem ? { ...existingItem, ...item } : item;
    });

    return result;
};

const deleteEntities = <T extends {}>(source: Entities<T>, action: DeleteEntities, key: string): Entities<T> => {
    if (action.entityName === key) {
        return deleteEntityImp(source, action.entityIds);
    }
    return source;
};

const deleteEntityForObjectNotFound = <T extends {}>(
    source: Entities<T>,
    action: ObjectNotFoundAction,
    params: EntityReducerParams
) => {
    if (params.entityName && action.body.EntityName === params.entityName) {
        return deleteEntityImp(source, [action.body.ObjectKey]);
    }
    return source;
};

const deleteEntityImp = <T extends {}>(source: Entities<T>, ids: (number | string)[]): Entities<T> => {
    if (!source) {
        return source;
    }

    return omit(source, ids);
};

export interface EntityReducerParams {
    entityName?: string;
    ignoreCleanEntities?: boolean;
    initState?: any;
}

const substituteEntityOnCreateSuccess = <T extends {}>(
    state: Entities<T>,
    action: CreateEntitySuccess,
    key: string
): Entities<T> => {
    if (key !== action.entityName || !state || !state[action.oldEntityId]) {
        return state;
    }
    const newEntity = { ...(state[action.oldEntityId] as {}), ...action.entity };

    const result = { ...state };
    delete result[action.oldEntityId];

    result[action.entity.Id] = newEntity as any;
    return result;
};

type CreateEntityReducer = <TEntities = Entities<any>>(
    key: string,
    params?: EntityReducerParams
) => Reducer<TEntities, EntitiesActions | ObjectNotFoundAction>;
export const createEntityReducer: CreateEntityReducer = (key, params = { entityName: null }) => {
    const { initState = {} } = params;
    return (state: any = initState, action) => {
        switch (action.type) {
            case EntitiesActionTypes.UPDATE_ENTITIES: {
                let entities = action.entities[key];
                // init entities if entityKey is match even if there is no
                // corresponding entity in action
                if (!entities && action.entityKey === key) {
                    entities = {};
                }

                return mergeEntity(state, entities);
            }
            case EntitiesActionTypes.RECONSTRUCT_ENTITIES: {
                if (action.entities[key] || key === action.entityKey) {
                    return mergeEntity({}, action.entities[key] || {});
                }
                return state;
            }
            case EntitiesActionTypes.DELETE_ENTITIES: {
                return deleteEntities(state, action, key);
            }
            case apiMiddlewareActionTypes.API_OBJECT_NOT_FOUND: {
                return deleteEntityForObjectNotFound(state, action, params);
            }
            case EntitiesActionTypes.CREATE_ENTITY_SUCCESS: {
                return substituteEntityOnCreateSuccess(state, action, key);
            }
            case EntitiesActionTypes.CLEAN_ENTITIES: {
                return params.ignoreCleanEntities ? state : initState;
            }
            default:
                return state;
        }
    };
};
